/**
 * Legal AI Service (Frontend)
 * Client-side service for interacting with the Legal AI backend endpoints
 */

// =============================================================================
// Types (matching backend types)
// =============================================================================

export type AIProvider = 'anthropic' | 'openai';

export type AIModel =
  // Anthropic Claude models
  | 'claude-opus-5-5'
  | 'claude-sonnet-5-5'
  | 'claude-haiku-4-5'
  // OpenAI models
  | 'gpt-5.1'
  | 'gpt-4.1'
  | 'gpt-5-mini';

export type LLMMode =
  | 'standard'           // General review and improvements
  | 'legal-deep-dive'    // Detailed legal reasoning & risk detection
  | 'simplified'         // Plain-language rewrites
  | 'jurisdiction'       // Region-specific guidance
  | 'negotiation';       // Strengthen/soften clauses

// =============================================================================
// Model Configuration
// =============================================================================

export interface ModelConfig {
  id: AIModel;
  name: string;
  provider: AIProvider;
  description: string;
  tier: 'premium' | 'standard' | 'economy';
}

export const AI_MODELS: ModelConfig[] = [
  // Anthropic Claude models
  {
    id: 'claude-opus-5-5',
    name: 'Claude Opus 5.5',
    provider: 'anthropic',
    description: 'Most capable model for complex legal analysis',
    tier: 'premium'
  },
  {
    id: 'claude-sonnet-5-5',
    name: 'Claude Sonnet 5.5',
    provider: 'anthropic',
    description: 'Balanced performance and speed',
    tier: 'standard'
  },
  {
    id: 'claude-haiku-4-5',
    name: 'Claude Haiku 4.5',
    provider: 'anthropic',
    description: 'Fast responses for simple queries',
    tier: 'economy'
  },
  // OpenAI models
  // gpt-5-pro is Responses-API only and can't be served by the chat backend.
  {
    id: 'gpt-5.1',
    name: 'ChatGPT 5.1',
    provider: 'openai',
    description: 'Most capable OpenAI model available here',
    tier: 'premium'
  },
  {
    id: 'gpt-4.1',
    name: 'GPT-4.1',
    provider: 'openai',
    description: 'Balanced OpenAI model',
    tier: 'standard'
  },
  {
    id: 'gpt-5-mini',
    name: 'ChatGPT 5 Mini',
    provider: 'openai',
    description: 'Fast and efficient',
    tier: 'economy'
  }
];

export const getModelsByProvider = (provider: AIProvider): ModelConfig[] => {
  return AI_MODELS.filter(m => m.provider === provider);
};

export const getModelConfig = (modelId: AIModel): ModelConfig | undefined => {
  return AI_MODELS.find(m => m.id === modelId);
};

export interface ClauseInfo {
  reference: string;
  title: string;
  summary?: string;
  fullText?: string;
  partyObligations?: string;
  riskLevel?: 'high' | 'medium' | 'low';
  riskDetails?: {
    legalRisk?: string;
    financialExposure?: string;
    operationalImpact?: string;
  };
  negotiationStatus?: {
    favorability?: string;
    classification?: string;
    recommendedModification?: string;
  };
  actionableRecommendations?: string[];
}

export interface ContractAnalysisSummary {
  overview?: string;
  overallScore?: number;
  criticalIssues?: Array<{
    title: string;
    description: string;
    riskLevel: string;
    recommendedAction?: string;
  }>;
}

export interface ContractContext {
  contractId?: string;
  title?: string;
  type?: string;
  jurisdiction?: string;
  parties?: {
    buyer?: string;
    supplier?: string;
  };
  effectiveDate?: string;
  expirationDate?: string;
  contractValue?: number;
  riskFlags?: string[];
  // Full contract analysis data
  executiveSummary?: ContractAnalysisSummary;
  clauses?: ClauseInfo[];
  // Raw contract text (if available)
  rawContractText?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
}

export interface LegalAIChatRequest {
  message: string;
  mode: LLMMode;
  provider?: AIProvider;
  model?: string;
  contractContext?: ContractContext;
  conversationHistory?: ChatMessage[];
  clauseText?: string;
  jurisdictionCode?: string;
}

export interface LegalAIChatResponse {
  content: string;
  suggestions?: string[];
  warnings?: string[];
  metadata: {
    provider: AIProvider;
    model: string;
    mode: LLMMode;
    tokensUsed?: number;
    processingTimeMs: number;
  };
}

export interface ClauseAnalysisResponse {
  originalText: string;
  analysis: string;
  riskLevel?: 'low' | 'medium' | 'high';
  suggestions?: {
    suggestedText: string;
    rationale: string;
    impact: string;
  }[];
  complianceIssues?: {
    regulation: string;
    issue: string;
    recommendation: string;
  }[];
  metadata: {
    provider: AIProvider;
    model: string;
    processingTimeMs: number;
  };
}

export interface ClauseImprovementResponse {
  original: string;
  improved: string;
  changes: string[];
}

export interface NegotiationAlternativesResponse {
  alternatives: Array<{
    text: string;
    rationale: string;
    impact: string;
  }>;
}

export type QuickAction = 
  | 'review-risks'
  | 'improve-clarity'
  | 'make-gdpr-compliant'
  | 'simplify-language'
  | 'negotiation-suggestions'
  | 'check-compliance'
  | 'compare-market-standard';

export interface LegalAIStatus {
  available: boolean;
  providers: AIProvider[];
  modes: LLMMode[];
}

// =============================================================================
// Mode Display Configuration
// =============================================================================

export const LLM_MODE_CONFIG: Record<LLMMode, {
  label: string;
  description: string;
  icon: string;
  color: string;
}> = {
  'standard': {
    label: 'Standard Review',
    description: 'General improvements and clarity analysis',
    icon: 'FileText',
    color: 'blue'
  },
  'legal-deep-dive': {
    label: 'Legal Deep-Dive',
    description: 'Detailed legal reasoning & risk detection',
    icon: 'Scale',
    color: 'purple'
  },
  'simplified': {
    label: 'Simplified Language',
    description: 'Plain-language rewrites anyone can understand',
    icon: 'MessageSquare',
    color: 'green'
  },
  'jurisdiction': {
    label: 'Jurisdiction Mode',
    description: 'Region-specific legal guidance (EU, UK, US)',
    icon: 'Globe',
    color: 'orange'
  },
  'negotiation': {
    label: 'Negotiation Mode',
    description: 'Strengthen or soften clauses for your position',
    icon: 'Handshake',
    color: 'red'
  }
};

export const QUICK_ACTION_CONFIG: Record<QuickAction, {
  label: string;
  description: string;
  icon: string;
}> = {
  'review-risks': {
    label: 'Review Risks',
    description: 'Identify all potential risks and liability exposure',
    icon: 'AlertTriangle'
  },
  'improve-clarity': {
    label: 'Improve Clarity',
    description: 'Suggest clearer language without changing meaning',
    icon: 'Edit'
  },
  'make-gdpr-compliant': {
    label: 'GDPR Compliance',
    description: 'Check and fix GDPR compliance issues',
    icon: 'Shield'
  },
  'simplify-language': {
    label: 'Simplify Language',
    description: 'Rewrite in plain, simple language',
    icon: 'MessageCircle'
  },
  'negotiation-suggestions': {
    label: 'Negotiation Help',
    description: 'Get alternative language to strengthen your position',
    icon: 'Target'
  },
  'check-compliance': {
    label: 'Check Compliance',
    description: 'Verify regulatory compliance across jurisdictions',
    icon: 'CheckCircle'
  },
  'compare-market-standard': {
    label: 'Market Comparison',
    description: 'Compare against industry standard terms',
    icon: 'BarChart'
  }
};

// =============================================================================
// API Helper Functions
// =============================================================================

const getApiUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;
  if (!apiUrl) {
    throw new Error('API URL is not configured. Please set REACT_APP_API_URL in your .env file.');
  }
  return apiUrl;
};

const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  const result = await response.json();
  
  if (!response.ok || !result.success) {
    if (response.status === 401) {
      throw new Error('Session expired. Please log in again.');
    }
    if (response.status === 429) {
      throw new Error('Rate limit exceeded. Please wait before trying again.');
    }
    if (response.status === 503) {
      throw new Error('AI service is temporarily overloaded. Please try again in a moment.');
    }
    throw new Error(result.message || 'Request failed');
  }
  
  return result.data;
};

// =============================================================================
// Service Functions
// =============================================================================

/**
 * Check Legal AI service status
 */
export async function checkLegalAIStatus(): Promise<LegalAIStatus> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse<LegalAIStatus>(response);
}

/**
 * Send a chat message to the Legal AI
 */
export async function sendLegalAIChat(request: LegalAIChatRequest): Promise<LegalAIChatResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(request)
  });

  return handleResponse<LegalAIChatResponse>(response);
}

/**
 * Stream event types for SSE
 */
export interface StreamChunkEvent {
  type: 'chunk';
  content: string;
}

export interface StreamDoneEvent {
  type: 'done';
  content: string;
  metadata: {
    provider: AIProvider;
    model: string;
    mode: LLMMode;
    processingTimeMs: number;
  };
}

export interface StreamErrorEvent {
  type: 'error';
  message: string;
}

export type StreamEvent = StreamChunkEvent | StreamDoneEvent | StreamErrorEvent;

/**
 * Send a streaming chat message to the Legal AI
 * Returns an async generator that yields stream events
 */
export async function* sendLegalAIChatStream(
  request: LegalAIChatRequest
): AsyncGenerator<StreamEvent, void, unknown> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6).trim();
          if (jsonStr) {
            try {
              const event = JSON.parse(jsonStr) as StreamEvent;
              yield event;
            } catch (e) {
              console.error('Failed to parse SSE event:', jsonStr);
            }
          }
        }
      }
    }

    // Process any remaining buffer
    if (buffer.startsWith('data: ')) {
      const jsonStr = buffer.slice(6).trim();
      if (jsonStr) {
        try {
          const event = JSON.parse(jsonStr) as StreamEvent;
          yield event;
        } catch (e) {
          console.error('Failed to parse final SSE event:', jsonStr);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Analyze a specific clause
 */
export async function analyzeClause(
  clauseText: string,
  analysisType: 'risk' | 'clarity' | 'compliance' | 'negotiation' | 'simplify',
  contractContext?: ContractContext,
  provider?: AIProvider
): Promise<ClauseAnalysisResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/analyze-clause`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      clauseText,
      analysisType,
      contractContext,
      provider
    })
  });

  return handleResponse<ClauseAnalysisResponse>(response);
}

/**
 * Improve a clause (clarity, protection, or balance)
 */
export async function improveClause(
  clauseText: string,
  improvementType: 'clarity' | 'protection' | 'balance',
  contractContext?: ContractContext,
  provider?: AIProvider
): Promise<ClauseImprovementResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/improve-clause`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      clauseText,
      improvementType,
      contractContext,
      provider
    })
  });

  return handleResponse<ClauseImprovementResponse>(response);
}

/**
 * Generate negotiation alternatives
 */
export async function generateNegotiationAlternatives(
  clauseText: string,
  position: 'strengthen' | 'soften' | 'balance',
  contractContext?: ContractContext,
  provider?: AIProvider
): Promise<NegotiationAlternativesResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/negotiation-alternatives`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      clauseText,
      position,
      contractContext,
      provider
    })
  });

  return handleResponse<NegotiationAlternativesResponse>(response);
}

/**
 * Execute a quick action
 */
export async function executeQuickAction(
  action: QuickAction,
  clauseText: string,
  contractContext?: ContractContext,
  provider?: AIProvider
): Promise<LegalAIChatResponse & { action: QuickAction }> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/quick-action`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      action,
      clauseText,
      contractContext,
      provider
    })
  });

  return handleResponse<LegalAIChatResponse & { action: QuickAction }>(response);
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format conversation history for display
 */
export function formatConversationForDisplay(messages: ChatMessage[]): Array<{
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}> {
  return messages
    .filter(m => m.role !== 'system')
    .map((m, index) => ({
      id: `msg-${index}`,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
    }));
}

/**
 * Create a contract context from a contract object with full analysis data
 */
export function createContractContext(contract: any, jsonAnalysis?: any): ContractContext {
  const context: ContractContext = {
    contractId: contract._id || contract.id,
    title: contract.contractTitle || contract.title,
    type: contract.contractType || contract.type,
    jurisdiction: contract.jurisdiction,
    parties: {
      buyer: contract.buyerName || 'Our Company',
      supplier: contract.supplier?.companyName || contract.supplierName
    },
    effectiveDate: contract.startDate || contract.effectiveDate,
    expirationDate: contract.endDate || contract.expirationDate,
    contractValue: contract.value || contract.contractValue,
    riskFlags: contract.riskFlags || []
  };

  // Add full analysis data if available
  const analysis = jsonAnalysis || contract.jsonAnalysis;
  if (analysis) {
    // Add executive summary
    if (analysis.executive_summary) {
      context.executiveSummary = {
        overview: analysis.executive_summary.overview,
        overallScore: analysis.executive_summary.overall_favorability_score,
        criticalIssues: analysis.executive_summary.critical_issues?.map((issue: any) => ({
          title: issue.title,
          description: issue.description,
          riskLevel: issue.risk_level,
          recommendedAction: issue.recommended_action
        }))
      };
    }

    // Add clause details
    if (analysis.clauses && Array.isArray(analysis.clauses)) {
      context.clauses = analysis.clauses.map((clause: any) => {
        const clauseInfo: ClauseInfo = {
          reference: clause.reference || '',
          title: clause.title || clause.full_heading || '',
          summary: clause.summary,
          partyObligations: clause.party_obligations,
          riskLevel: clause.risk_assessment?.legal_risk?.toLowerCase().includes('high') ? 'high' :
                     clause.risk_assessment?.legal_risk?.toLowerCase().includes('medium') ? 'medium' : 'low'
        };

        // Add risk details
        if (clause.risk_assessment) {
          clauseInfo.riskDetails = {
            legalRisk: clause.risk_assessment.legal_risk,
            financialExposure: clause.risk_assessment.financial_exposure,
            operationalImpact: clause.risk_assessment.operational_impact
          };
        }

        // Add negotiation status
        if (clause.negotiation_analysis) {
          clauseInfo.negotiationStatus = {
            favorability: clause.negotiation_analysis.favorability,
            classification: clause.negotiation_analysis.classification,
            recommendedModification: clause.negotiation_analysis.recommended_modification
          };
        }

        // Add actionable recommendations
        if (clause.actionable_recommendations) {
          const recs: string[] = [];
          if (clause.actionable_recommendations.immediate_actions) {
            recs.push(`Immediate: ${clause.actionable_recommendations.immediate_actions}`);
          }
          if (clause.actionable_recommendations.approvals_needed) {
            recs.push(`Approvals: ${clause.actionable_recommendations.approvals_needed}`);
          }
          if (clause.actionable_recommendations.implementation_notes) {
            recs.push(`Implementation: ${clause.actionable_recommendations.implementation_notes}`);
          }
          if (recs.length > 0) {
            clauseInfo.actionableRecommendations = recs;
          }
        }

        return clauseInfo;
      });
    }
  }

  return context;
}

/**
 * Get appropriate jurisdiction code from jurisdiction string
 */
export function getJurisdictionCode(jurisdiction: string): string | undefined {
  const jurisdictionMap: Record<string, string> = {
    'eu': 'EU',
    'european union': 'EU',
    'europe': 'EU',
    'uk': 'UK',
    'united kingdom': 'UK',
    'england': 'UK',
    'wales': 'UK',
    'scotland': 'UK',
    'us': 'US',
    'usa': 'US',
    'united states': 'US',
    'california': 'US-CA',
    'new york': 'US-NY',
    'texas': 'US-TX',
    'delaware': 'US-DE'
  };

  const normalized = jurisdiction.toLowerCase().trim();
  return jurisdictionMap[normalized];
}

// =============================================================================
// Chat History Types
// =============================================================================

export interface ChatHistoryMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: {
    model?: string;
    mode?: string;
    processingTimeMs?: number;
  };
}

export interface Conversation {
  _id: string;
  contractId: string;
  userId: string;
  title: string;
  messages: ChatHistoryMessage[];
  aiModel: string;
  aiMode: string;
  createdAt: string;
  updatedAt: string;
  isArchived: boolean;
  messageCount?: number;
  lastMessage?: string;
}

export interface ConversationSummary {
  _id: string;
  title: string;
  aiModel: string;
  aiMode: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessage: string;
}

// =============================================================================
// Chat History API Functions
// =============================================================================

/**
 * Get all conversations for a contract
 */
export async function getConversations(contractId: string, includeArchived: boolean = false): Promise<ConversationSummary[]> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const url = `${getApiUrl()}/api/legal-ai/conversations/${contractId}${includeArchived ? '?includeArchived=true' : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse<ConversationSummary[]>(response);
}

/**
 * Get a single conversation with all messages
 */
export async function getConversation(conversationId: string): Promise<Conversation> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/conversation/${conversationId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse<Conversation>(response);
}

/**
 * Create a new conversation
 */
export async function createConversation(
  contractId: string,
  title?: string,
  model?: string,
  mode?: string
): Promise<Conversation> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ contractId, title, model, mode })
  });

  return handleResponse<Conversation>(response);
}

/**
 * Add a message to a conversation
 */
export async function addMessageToConversation(
  conversationId: string,
  role: 'user' | 'assistant' | 'system',
  content: string,
  metadata?: { model?: string; mode?: string; processingTimeMs?: number }
): Promise<{ conversation: Conversation; addedMessage: ChatHistoryMessage }> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/conversation/${conversationId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ role, content, metadata })
  });

  return handleResponse<{ conversation: Conversation; addedMessage: ChatHistoryMessage }>(response);
}

/**
 * Update a conversation (title, archive status)
 */
export async function updateConversation(
  conversationId: string,
  updates: { title?: string; isArchived?: boolean }
): Promise<Conversation> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/conversation/${conversationId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(updates)
  });

  return handleResponse<Conversation>(response);
}

/**
 * Delete a conversation
 */
export async function deleteConversation(conversationId: string): Promise<{ message: string }> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const response = await fetch(`${getApiUrl()}/api/legal-ai/conversation/${conversationId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse<{ message: string }>(response);
}

// =============================================================================
// Conversation Search
// =============================================================================

export interface MatchingMessage {
  role: 'user' | 'assistant';
  preview: string;
  timestamp: string;
}

export interface ConversationSearchResult {
  _id: string;
  title: string;
  contractId: string;
  aiModel: string;
  aiMode: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  matchingMessages: MatchingMessage[];
  titleMatch: boolean;
}

export interface ConversationSearchResponse {
  results: ConversationSearchResult[];
  pagination: {
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  };
}

/**
 * Search conversations across all contracts or within a specific contract
 */
export async function searchConversations(
  query: string,
  options: {
    contractId?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<ConversationSearchResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  const params = new URLSearchParams();
  params.append('query', query);
  if (options.contractId) params.append('contractId', options.contractId);
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());

  const response = await fetch(`${getApiUrl()}/api/legal-ai/conversations/search?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse<ConversationSearchResponse>(response);
}

// =============================================================================
// Insert to Contract
// =============================================================================

/**
 * Add AI-generated content as a note to a contract
 */
export async function addAINoteToContract(
  contractId: string,
  content: string,
  source: 'legal-ai' | 'quick-action' | 'clause-analysis' = 'legal-ai'
): Promise<{ noteId: string; message: string }> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  // Prefix the content to indicate it's AI-generated
  const aiPrefix = `[AI-Generated - ${source.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}]\n\n`;
  const fullContent = aiPrefix + content;

  const response = await fetch(`${getApiUrl()}/api/contracts/${contractId}/notes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      content: fullContent,
      isInternal: true
    })
  });

  const data = await handleResponse<{ _id: string }>(response);
  return {
    noteId: data._id,
    message: 'AI content added as contract note'
  };
}
